import { GroupCode, GroupSecurity } from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import {
  type PaginationQueryParams,
  PaginationSchema,
} from '../domain/pagination';
import type { PaginatedBundle } from '../domain/schema';

export interface GroupAddMembersRequest {
  /**
   * List of member ids to add to the group
   */
  members: GroupMembers[];
}

export const GroupAddMembersResponseSchema = z.object({
  members: z.array(DisplayReferenceSchema),
});

const GroupMembersSchema = z.object({
  id: z.string(),
  display: z.string().optional(),
});

export const GroupUpdateMemberRequestSchema = z.object({
  id: UUIDSchema,
  members: z.array(GroupMembersSchema),
  active: z.boolean(),
});

export interface GroupGetAllResponse extends PaginatedBundle {
  groups: GroupGetResponse[];
}

export interface GroupGetAllParams extends PaginationQueryParams {
  code?: GroupCode;
}

const GroupTypeSchema = z.nativeEnum(GroupCode);
const GroupSecuritySchema = z.nativeEnum(GroupSecurity);

export const GroupSearchSchema = PaginationSchema.extend({
  code: GroupTypeSchema.optional(),
});

const MemberSchema = z.object({
  id: UUIDSchema,
  active: z.boolean(),
  display: z.string().optional(),
});

export const GroupSchema = z.object({
  active: z.boolean(),
  members: MemberSchema.array(),
  name: z.string(),
  type: GroupTypeSchema,
  organization: DisplayReferenceSchema,
  permissions: GroupSecuritySchema.array(),
});

export const GroupGetResponseSchema = GroupSchema.partial().extend({
  id: UUIDSchema,
});

export const GroupGetAllResponseSchema = z.object({
  groups: GroupGetResponseSchema.array(),
});

export const GroupCreateRequestSchema = GroupSchema.omit({
  organization: true,
  members: true,
}).extend({
  organization: UUIDSchema,
});

export const GroupUpdateRequestSchema = GroupCreateRequestSchema.extend({
  id: UUIDSchema,
});

export type Group = z.infer<typeof GroupSchema>;
export type GroupSearchParams = z.infer<typeof GroupSearchSchema>;
export type GroupAddMembersResponse = z.infer<
  typeof GroupAddMembersResponseSchema
>;

export type GroupUpdateMemberRequest = z.infer<
  typeof GroupUpdateMemberRequestSchema
>;

export type GroupUpdateMemberResponse = GroupAddMembersResponse;

export type GroupCreateRequest = z.infer<typeof GroupCreateRequestSchema>;

export type GroupMember = z.infer<typeof MemberSchema>;

export type GroupMembers = z.infer<typeof GroupMembersSchema>;

export type GroupGetResponse = z.infer<typeof GroupGetResponseSchema>;

export type GroupUpdateRequest = z.infer<typeof GroupUpdateRequestSchema>;

export type GroupCreateResponse = GroupGetResponse;

export type GroupUpdateResponse = GroupGetResponse;
